/**
 * The walkers (02 §5): Zen-chan in full, and the walking of Hidegons, Drunk and Mighta, whose AI
 * is the same routine once their attack (M2) is idle. Ported from L_EA08 (Zen-chan's state handler;
 * the others enter it from D_E9FD after the attack logic D_EDCD).
 *
 * One update: look at the player (D_EE62); keep falling or jumping if it is; else, when allowed,
 * look for a platform 2..5 rows up to jump to; fall if nothing is underfoot; gap-jump if a ledge is
 * ahead with floor beyond; else walk by its joystick bits, turning only at walls.
 */

import { watchPlayer } from './chase';
import { hit, hitRow, OFF, platformAbove, probe, type Probe } from './map';
import { jumpTick, startVerticalJump } from './jump';
import { animate, fallStep } from './motion';
import { GAP_REACH, GAP_START, TOP_Y, WALK_STEP } from './tuning';
import type { Monster, PlayerId, World } from '../types';

/**
 * L_EA92: the gap jump, when either permission is set (never for Hidegons, state 3). Facing right
 * it goes from 6 px over the column grid when the column ahead (col+2) is open under the feet and
 * col+8 is solid; facing left from on the grid, col-1 open and col-10 solid (the C64's own
 * asymmetric reach: its pointer +9 / -9, L_EA92..D_EAD1). `frame0` is the frame at the tick start.
 */
function tryGapJump(world: World, m: Monster, p: Probe, frame0: number): boolean {
  const a = m.ai;
  if ((!a.gapOk && !a.vertOk) || m.kind === 'hidegons') return false;
  const right = frame0 < a.faceFrames;
  if (right) {
    if (p.subX !== 6 || hit(world, p, OFF.feetRight) || !hit(world, p, OFF.feetLeft + GAP_REACH)) return false;
  } else if (p.subX !== 0 || hit(world, p, OFF.feetLeft) || !hit(world, p, OFF.feetLeft - GAP_REACH)) {
    return false;
  }
  a.driftL = right ? 0 : -1;
  a.driftR = right ? -1 : 0;
  a.jump = GAP_START;
  jumpTick(world, m, p);
  return true;
}

/**
 * L_ECF7: step `dx` unless on the column grid with a wall in cell `off` (beside the lowest body
 * row); a wall flips the joystick's left/right bits instead - the turn happens next tick.
 */
function step(world: World, m: Monster, p: Probe, off: number, dx: number): void {
  if (p.subX !== 0 || !hit(world, p, off)) m.x = (m.x + dx) & 0xff;
  else m.ai.joy ^= 3;
}

/**
 * D_EAFA: walk. Bit 0 (left): facing right, it only turns (D_ECDF); else steps left. Bit 1 (right):
 * the same the other way (D_ED1E). Bit 2: start the vertical jump.
 */
function walk(world: World, m: Monster, p: Probe, joy: number, frame0: number): void {
  const a = m.ai;
  if (joy & 1) {
    if (frame0 < a.faceFrames) a.frame = a.faceFrames;
    else step(world, m, p, p.subY === 0 ? OFF.lowLeft : OFF.feetLeft, -WALK_STEP);
  }
  if (joy & 2) {
    if (frame0 >= a.faceFrames) a.frame = 0;
    else step(world, m, p, p.subY === 0 ? OFF.lowRight : OFF.feetRight, WALK_STEP);
  }
  if (joy & 4) {
    startVerticalJump(world, m, p);
    return;
  }
  animate(m);
}

/** L_EA08: one AI update of a walker hunting player `target`. */
export function walkerUpdate(world: World, m: Monster, target: PlayerId): void {
  const a = m.ai;
  const p = probe(m);
  const frame0 = a.frame;
  watchPlayer(world, m, target);
  if (a.falling) {
    if (fallStep(world, m, p)) a.falling = false;
    animate(m);
    return;
  }
  if (a.jump >= 0) {
    jumpTick(world, m, p);
    return;
  }
  if (a.vertOk && platformAbove(world, p)) a.joy |= 4;
  const grounded = m.y >= TOP_Y && (p.subY !== 0 || hitRow(world, p, OFF.floor));
  if (!grounded) {
    // L_EA8C: start falling, and fall this tick.
    a.falling = true;
    if (fallStep(world, m, p)) a.falling = false;
    animate(m);
    return;
  }
  if (p.subY === 0 && tryGapJump(world, m, p, frame0)) return;
  walk(world, m, p, a.joy, frame0);
}
